import { lazy, useEffect, useRef, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { useNVAlertsContext } from '@nv/contexts'
import EditIcon from '@nv/images/design/edit-icon'
import FacultyIcon from '@nv/images/design/faculty-icon'
import PinIcon from '@nv/images/design/pin-icon'
import ThumbsUpIcon from '@nv/images/design/thumbs-up-icon'
import { NominatorService } from '@nv/services'
import { useTranslation } from 'react-i18next'
import useNVModal from '../modal_master/nv_modal/useNVModal'
import Skeleton from '../skeleton/Skeleton'
import styles from './CandidateProfileHeader.module.scss'
const NVBasicModal = lazy(() => import('@nv/lazy-loadable/lib/modals/NVBasicModal'))

export const CandidateProfileHeader = ({
  candidacy,
  loading,
  editEnabled,
  showEndorse,
  endorsedByUser,
  fetchCandidacy,
  endorseButtonEnabled,
  profileImageUrl,
  onFileSelected,
  candidacyState,
}: any) => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language
  const fileInputRef = useRef(null)
  const { showAlert } = useNVAlertsContext()
  const { NVModal: ConfirmEndorseModal, openModal: openConfirmEndorseModal, closeModal: closeConfirmEndorseModal } = useNVModal()
  const { NVModal: ConfirmUnendorseModal, openModal: openConfirmUnendorseModal, closeModal: closeConfirmUnendorseModal } = useNVModal()
  const candidacyId = candidacy?.nominee ? candidacy?.id : null
  const candidateData = candidacy?.nominee ? candidacy?.nominee : candidacy
  const [localPreview, setLocalPreview] = useState(null)

  // Sync server URL into preview ONLY if no local file
  useEffect(() => {
    if (!localPreview) setLocalPreview(profileImageUrl)
  }, [profileImageUrl])

  const handleEditClick = () => fileInputRef.current?.click()

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validTypes = ['image/jpeg', 'image/png']
    const maxSize = 5242880 // MAX 5MB profile images

    if (!validTypes.includes(file.type) || file.size > maxSize) {
      showAlert({ message: t('events.profile.errors.updateFailed'), type: 'error' })
      return
    }

    const previewUrl = URL.createObjectURL(file)
    setLocalPreview(previewUrl)
    onFileSelected(file)
  }

  const endorseCandidate = async () => {
    const res = await NominatorService.endorseCandidate(candidacyId)
    if (res?.success) {
      showAlert({ message: t('events.candidateEvents.candidateEndorsedSuccess'), icon: <ThumbsUpIcon /> })
    }
    fetchCandidacy()
  }

  const unEndorseCandidate = async () => {
    const res = await NominatorService.unEndorseCandidate(candidacyId)
    if (res?.success) {
      showAlert({
        message: t('events.candidateEvents.candidateUnEndorsedSuccess'),
        icon: <ThumbsUpIcon style={{ transform: 'scaleY(-1)' }} />,
      })
    }
    fetchCandidacy()
  }

  if (!candidacy) return null

  return (
    <>
      <ConfirmEndorseModal>
        <NVBasicModal
          title={t('buttons.endorse')}
          icon={<ThumbsUpIcon />}
          text={t('events.candidateEvents.areYouSureEndorse')}
          showCancel
          onConfirm={() => {
            endorseCandidate()
            closeConfirmEndorseModal()
          }}
        />
      </ConfirmEndorseModal>

      <ConfirmUnendorseModal>
        <NVBasicModal
          title={t('buttons.unEndorse')}
          icon={<ThumbsUpIcon style={{ transform: 'scaleY(-1)' }} />}
          text={t('events.candidateEvents.areYouSureUnEndorse')}
          showCancel
          onConfirm={() => {
            unEndorseCandidate()
            closeConfirmUnendorseModal()
          }}
        />
      </ConfirmUnendorseModal>
      <div className={styles['container']}>
        <div className={styles['image-section']}>
          <div className={styles['image']}>
            {localPreview ? <img src={localPreview} alt='Profile preview' /> : <Skeleton width={200} height={200} />}
          </div>
          {editEnabled && (
            <NVButton variant='iconOnly' classes={{ container: styles['edit-icon'] }} icon={<EditIcon />} onClick={handleEditClick} />
          )}
          <input type='file' accept='image/*' onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} />
          <div className={styles['badge']}>{candidateData?.userType}</div>
          {editEnabled && (
            <div className={styles['requirements']}>
              {t('events.profile.requirements')}
              <div>• {t('events.profile.jpgorpng')}</div>
              <div>• {t('events.profile.maxSize')}</div>
              <div>• {t('events.profile.squareImage')}</div>
            </div>
          )}
        </div>

        <div className={styles['info']}>
          <div className={styles['name']}>{loading ? <Skeleton width={100} height={20} /> : candidateData?.fullName}</div>
          <div className={styles['states']}>
            <div className={styles['state']}>
              <FacultyIcon /> {loading ? <Skeleton width={40} height={20} /> : candidateData?.group?.abbreviation[currentLanguage] || ''}
            </div>
            <div className={styles['state']}>
              <FacultyIcon /> {loading ? <Skeleton width={40} height={20} /> : candidateData?.subgroup?.abbreviation[currentLanguage] || ''}
            </div>
            <div className={styles['state']}>
              <PinIcon /> {loading ? <Skeleton width={40} height={20} /> : candidateData?.region?.abbreviation[currentLanguage] || ''}
            </div>
            <div className={styles['state']}>
              <PinIcon /> {loading ? <Skeleton width={40} height={20} /> : candidateData?.branch?.abbreviation[currentLanguage] || ''}
            </div>
          </div>

          {showEndorse &&
            (endorsedByUser ? (
              <NVButton
                icon={<ThumbsUpIcon style={{ stroke: 'var(--color-white)' }} />}
                text={t('buttons.nominated')}
                height={40}
                classes={{ container: styles['endorse'] }}
                onClick={openConfirmUnendorseModal}
                disabled={!endorseButtonEnabled || candidacyState === 'admin-approved'}
              />
            ) : (
              <NVButton
                variant='cancel'
                icon={<ThumbsUpIcon style={{ stroke: 'var(--color-white)' }} />}
                text={t('buttons.nominate')}
                height={40}
                classes={{ container: styles['endorse'] }}
                onClick={openConfirmEndorseModal}
                disabled={!endorseButtonEnabled}
              />
            ))}
        </div>
      </div>
    </>
  )
}
